import {AxiosRequestConfig, AxiosResponse} from 'axios';
import {HealthMeasurementType} from '../backend/enums/health-measurement-type';
import {Product} from '../backend/enums/product';
import {RelationshipDirection} from '../backend/enums/relationship-direction';
import {UserType} from '../backend/enums/user-type';
import {AffiliationInfo} from '../backend/interfaces/affiliation-info';
import {AffiliationInviteData} from '../backend/interfaces/affiliation-invite-data';
import {HealthMeasurement} from '../backend/interfaces/health-measurement';
import {HealthProfile} from '../backend/interfaces/health-profile';
import {Invite} from '../backend/interfaces/invite';
import {RelationshipInfo} from '../backend/interfaces/relationship-info';
import {User} from '../backend/interfaces/user';
import {PatchHealthProfile} from '../backend/requests/patch-health-profile';
import {PostCaregiver} from '../backend/requests/post-caregiver';
import {PostCaregiverAccount} from '../backend/requests/post-caregiver-account';
import {PostSenior} from '../backend/requests/post-senior';
import {PutUser} from '../backend/requests/put-user';
import {AffiliatedUser} from '../backend/responses/affiliated-user';
import {UserPublicInfo} from '../backend/responses/user-public-info';
import {SessionDatabase, SessionDatabaseStore} from '../databases/session';
import {BackendApiManager} from '../managers/backend-api';
import {MAX_LIMIT_ENTRIES} from '../utils/constants';
import {Logger} from '../utils/managers/logger';
import {PaginatedArrayResponse} from './interfaces/paginated-array-response';

export class UsersRepository {
  // Singleton Instance
  private static _instance: UsersRepository;

  // Private
  private logger: Logger;

  static get instance(): UsersRepository {
    if (!this._instance) {
      this._instance = new UsersRepository();
    }
    return this._instance;
  }

  constructor() {
    // Create Logger
    this.logger = new Logger('UsersRepository');
  }

  // Create an account by providing a voucher code
  public async createAccount(body: PostCaregiverAccount): Promise<User> {
    // Create user
    const response: AxiosResponse<User> = await BackendApiManager.instance.post<User>(
      '/users',
      body
    );

    // Return
    return response.data;
  }

  // Get all users in an entity
  public async getInEntity(
    entityId: string,
    limit: number,
    skip: number,
    type?: UserType,
    nameSearch?: string,
    useSafeDisplayNames?: boolean,
    filters?: string, // encoded string
    sort?: string // encoded string
  ): Promise<PaginatedArrayResponse<AffiliatedUser>> {
    // Set url
    let url: string = `/entities/${entityId}/users?skip=${skip}&limit=${limit}`;

    // Check text search
    if (nameSearch && nameSearch.trim().length !== 0) {
      // Add to the url
      url += `&name=${nameSearch}`;
    }

    // Type
    if (type) {
      // Add to the url
      url += `&type=${type}`;
    }

    // useSafeDisplayNames
    if (useSafeDisplayNames) {
      // Add to the url
      url += `&useSafeDisplayNames=${useSafeDisplayNames}`;
    }

    // Append enconded filters
    if (filters) url += `&${filters}`;

    // Append enconded sort
    if (sort) url += `&sort=${sort}`;

    // Get users from backend
    const response: AxiosResponse<Array<AffiliatedUser>> =
      await BackendApiManager.instance.get<Array<AffiliatedUser>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Get all users in an entity
  public async getAffiliatedUsers(
    entityId: string,
    query?: {
      type?: UserType;
      activationStates?: Array<boolean>;
      limit?: number;
      skip?: number;
    }
  ): Promise<PaginatedArrayResponse<AffiliatedUser>> {
    const skip = query?.skip || 0;
    const limit = query?.limit || MAX_LIMIT_ENTRIES;

    // Set url
    // # NOTE: if you change this url, change the regex in this.clearAffliatedUsersSessionCache() method
    let url: string = `/entities/${entityId}/users?skip=${skip}&limit=${limit}`;

    // Type
    if (query?.type) {
      // Add to the url
      url += `&type=${query?.type}`;
    }

    // activationStates
    if (query?.activationStates) {
      // Add to the url
      for (const element of query?.activationStates) {
        url += `&activationStates=${element}`;
      }
    }

    // Check Session Database
    const urlCache = await SessionDatabase.instance.getById<PaginatedArrayResponse<AffiliatedUser>>(
      SessionDatabaseStore.UrlCache,
      url
    );

    if (!urlCache) {
      // Get users from backend
      const response: AxiosResponse<Array<AffiliatedUser>> =
        await BackendApiManager.instance.get<Array<AffiliatedUser>>(url);

      const totalCount = response.headers['x-total-count']
        ? parseInt(response.headers['x-total-count'], 10)
        : response.data.length;

      // Save in Session Database
      await SessionDatabase.instance.put(SessionDatabaseStore.UrlCache, url, {
        data: response.data,
        totalCount: totalCount,
      });

      // Done
      return {
        data: response.data,
        totalCount: totalCount,
      };
    }

    return urlCache;
  }

  /**
   * Removes all `/entities/{entityId}/users` from Session Cache.
   */
  public async clearAffliatedUsersSessionCache(): Promise<void> {
    return SessionDatabase.instance.removeByMatch(
      SessionDatabaseStore.UrlCache,
      // See this.getAffiliatedUsers() for the actual endpoint being stored
      /^\/entities\/[^/]+\/users/i
    );
  }

  // Get all users in an entity
  public async getUserPublicInfo(userId: string): Promise<UserPublicInfo | null> {
    // Set url
    let url: string = `/users/${userId}/info`;

    // Check Session Database
    const urlCache = await SessionDatabase.instance.getById<UserPublicInfo>(
      SessionDatabaseStore.UrlCache,
      url
    );
    if (!urlCache) {
      try {
        // Get users from backend
        const response: AxiosResponse<UserPublicInfo> =
          await BackendApiManager.instance.get<UserPublicInfo>(url);

        // Save in Session Database
        await SessionDatabase.instance.put(SessionDatabaseStore.UrlCache, url, response.data);

        // Done
        return response.data;
      } catch (error) {
        return null;
      }
    }

    return urlCache;
  }

  // Get pending users in an entity
  public async getPendingInEntity(
    entityId: string,
    limit: number,
    skip: number,
    sort: 'createdAt' | 'destinationName',
    status?: 'to-do' | 'in-progress' | 'done' | 'failed',
    nameSearch?: string,
    useSafeDisplayNames?: boolean
  ): Promise<{totalCount: number; data: Invite<AffiliationInviteData>[]}> {
    // Set url
    let url: string = `/entities/${entityId}/affiliation-invites?skip=${skip}&limit=${limit}&sort=${sort}`;

    // status
    if (status) {
      // Add to the url
      url += `&status=${status}`;
    }

    // Check text search
    if (nameSearch && nameSearch.trim().length !== 0) {
      // Add to the url
      url += `&name=${nameSearch}`;
    }

    // useSafeDisplayNames
    if (useSafeDisplayNames) {
      // Add to the url
      url += `&useSafeDisplayNames=${useSafeDisplayNames}`;
    }

    // Get users from backend
    const response: AxiosResponse<Invite<AffiliationInviteData>[]> =
      await BackendApiManager.instance.get(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Get all users as system administrator
  public async getAsSystemAdministrator(
    limit: number,
    skip: number,
    type?: UserType,
    product?: Product,
    emailSearch?: string,
    nameSearch?: string
  ): Promise<{totalCount: number; data: Array<User>}> {
    // Set url
    let url: string = `/users?skip=${skip}&limit=${limit}`;

    // Name search
    if (nameSearch && nameSearch.trim().length !== 0) {
      // Add to the url
      url += `&name=${nameSearch}`;
    }

    // Email search
    if (emailSearch && emailSearch.trim().length !== 0) {
      // Add to the url
      url += `&email=${emailSearch}`;
    }

    // Type
    if (type) {
      // Add to the url
      url += `&type=${type}`;
    }

    // Product
    if (product) {
      // Add to the url
      url += `&product=${product}`;
    }

    // Get users from backend
    const response: AxiosResponse<Array<User>> =
      await BackendApiManager.instance.get<Array<User>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Get all user relationships
  public async getUserRelationships(
    userId: string,
    limit: number,
    skip: number,
    options?: {
      direction?: RelationshipDirection;
    }
  ): Promise<{totalCount: number; data: Array<RelationshipInfo>}> {
    // Set url
    let url: string = `/users/${userId}/relationships?skip=${skip}&limit=${limit}`;

    // Direction
    if (options?.direction) {
      // Add to the url
      url += `&direction=${options.direction}`;
    }

    // Get caregivers from backend
    const response: AxiosResponse<Array<RelationshipInfo>> =
      await BackendApiManager.instance.get<Array<RelationshipInfo>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Delete a relationship
  public async deleteUserRelationship(relationshipId: string): Promise<void> {
    // Delete relationship
    await BackendApiManager.instance.delete<void>(`/relationships/${relationshipId}`);
  }

  // Get a user info by id
  public async getInfoById(id: string, options?: AxiosRequestConfig): Promise<UserPublicInfo> {
    // Set url
    let url: string = `/users/${id}/info`;

    // Get user from backend
    const response: AxiosResponse<UserPublicInfo> =
      await BackendApiManager.instance.get<UserPublicInfo>(url, options);

    // Return
    return response.data;
  }

  // Update a user by id
  public async updateById(id: string, body: PutUser): Promise<User> {
    // Update user
    const response: AxiosResponse<User> = await BackendApiManager.instance.put<User>(
      `/users/${id}`,
      body
    );

    // Clear cache
    await this.clearAffliatedUsersSessionCache();

    // Return
    return response.data;
  }

  // Get a user by id
  public async getById(id: string): Promise<User> {
    // Get user
    const response: AxiosResponse<User> = await BackendApiManager.instance.get<User>(
      `/users/${id}`
    );

    // Return
    return response.data;
  }

  // Create a senior
  public async createSenior(entityId: string, body: PostSenior): Promise<User> {
    // Create senior
    const response: AxiosResponse<User> = await BackendApiManager.instance.post<User>(
      `/entities/${entityId}/users`,
      body
    );

    // Clear cache
    await this.clearAffliatedUsersSessionCache();

    // Return
    return response.data;
  }

  // Create a caregiver
  public async createCaregiver(entityId: string, body: PostCaregiver): Promise<User> {
    // Create senior
    const response: AxiosResponse<User> = await BackendApiManager.instance.post<User>(
      `/entities/${entityId}/users`,
      {
        ...body,
        type: UserType.Caregiver,
      }
    );

    // Return
    return response.data;
  }

  // Delete a user in an entity
  public async deleteUserInEntity(entityId: string, userId: string): Promise<void> {
    // Delete user
    await BackendApiManager.instance.delete<void>(`/entities/${entityId}/users/${userId}`);

    // Clear cache
    await this.clearAffliatedUsersSessionCache();
  }

  // Delete a user
  public async deleteById(userId: string): Promise<void> {
    // Delete user
    await BackendApiManager.instance.delete<void>(`/users/${userId}`);

    // Clear cache
    await this.clearAffliatedUsersSessionCache();
  }

  // Get the health profile of a user
  public async getUserHealthProfile(userId: string): Promise<HealthProfile | null> {
    // Get user health profile
    const response: AxiosResponse<HealthProfile | null> =
      await BackendApiManager.instance.get<HealthProfile | null>(`/users/${userId}/health-profile`);

    // Return
    return response.data;
  }

  // Update the health profile of a user
  public async updateUserHealthProfile(
    userId: string,
    body: PatchHealthProfile
  ): Promise<HealthProfile> {
    // Patch user health profile
    const response: AxiosResponse<HealthProfile> =
      await BackendApiManager.instance.patch<HealthProfile>(
        `/users/${userId}/health-profile`,
        body
      );

    // Return
    return response.data;
  }

  // Get User Health Measurements
  public async getUserHealthMeasurements(userId: string): Promise<Array<HealthMeasurement>> {
    // Get user health measurements
    const response: AxiosResponse<Array<HealthMeasurement>> = await BackendApiManager.instance.get<
      Array<HealthMeasurement>
    >(`/users/${userId}/health-measurements/latest`);

    // Return
    return response.data;
  }

  public async getAllUserHealthMeasurements(
    opts: {
      userId: string;
      startDate: string;
      endDate: string;
      types?: HealthMeasurementType[];
    },
    config?: AxiosRequestConfig
  ): Promise<Array<HealthMeasurement>> {
    const typesConcat = opts.types?.map((type) => `types=${type}`).join('&');
    const typesStr = typesConcat ? `&${typesConcat}` : '';

    // Get user health measurements
    const response: AxiosResponse<Array<HealthMeasurement>> = await BackendApiManager.instance.get<
      Array<HealthMeasurement>
    >(
      `/users/${opts.userId}/health-measurements?startDate=${opts.startDate}&endDate=${opts.endDate}${typesStr}`,
      config
    );

    // Return
    return response.data;
  }

  // Get all affiliations that user has
  public async getUserAffiliations(
    userId: string,
    skip: number,
    limit: number
  ): Promise<{totalCount: number; data: Array<AffiliationInfo>}> {
    // Get affiliations from backend
    const response: AxiosResponse<Array<AffiliationInfo>> = await BackendApiManager.instance.get<
      Array<AffiliationInfo>
    >(`/users/${userId}/affiliations?skip=${skip}&limit=${limit}`);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }
}
